import React, { Component } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import seedrandom from 'seedrandom';
import { lerp } from '../shared/math';
import { copyText } from '../shared/browser-utils';
import { ASSET_MODE_PER_CELL, ASSET_MODE_PER_ENTITY, Entity, ENTITY_KIND_ROAD } from '../shared/world';

const HALF_PI = Math.PI / 2;

const CELL_SIZE_METERS = 5;

const DIRECTIONS = {
  N: 0,
  E: HALF_PI,
  S: HALF_PI * 2,
  W: HALF_PI * 3,
};

const DIRECTION_MATRICES = {
  N: new THREE.Matrix4().makeRotationY(DIRECTIONS.N),
  E: new THREE.Matrix4().makeRotationY(DIRECTIONS.E),
  S: new THREE.Matrix4().makeRotationY(DIRECTIONS.S),
  W: new THREE.Matrix4().makeRotationY(DIRECTIONS.W),
};

// Amount of classes per transport type, starting from 0.
// E.g. cars have 0-1-2, so transport classes is 2.
const TRANSPORT_CLASSES = {
  pedestrians: 1,
  bikes: 1,
  cargoBikes: 1,
  cars: 2,
  trucks: 1,
  buses: 1,
  passengerTrains: 1,
  freightTrains: 1,
};

function binomialDistribution(p, maxAmount, rng) {
  p /= 100;
  let rank = 0;
  for (let j = 0; j < maxAmount; j++) {
    const v = rng();
    if (v < p) rank++;
  }
  return rank;
}

const skyVertexShader = `
varying vec3 vWorldPosition;

void main() {

  vec4 worldPosition = modelMatrix * vec4( position, 1.0 );
  vWorldPosition = worldPosition.xyz;

  gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );

}`;
const skyFragmentShader = `
uniform vec3 topColor;
uniform vec3 bottomColor;
uniform float offset;
uniform float exponent;

varying vec3 vWorldPosition;

void main() {

  float h = normalize( vWorldPosition + offset ).y;
  gl_FragColor = vec4( mix( bottomColor, topColor, max( pow( max( h , 0.0), exponent ), 0.0 ) ), 1.0 );

}
`;

export default class Viewport extends Component {
  constructor(props) {
    super(props);
    this.state = { camera: undefined };
    this.scene = undefined;
    this.camera = undefined;
    this.renderer = undefined;
    this.controls = undefined;
    this.targetCameraPosition = undefined;
    this.targetCameraQuaternion = undefined;
    this._animateCamera = this._animateCamera.bind(this);
    this._onResize = this._onResize.bind(this);
    this._onMouseMove = this._onMouseMove.bind(this);
    this._onMouseWheel = this._onMouseWheel.bind(this);
  }

  async componentDidMount() {
    const wrapper = document.querySelector('.viewport__wrapper');
    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.FogExp2(0xb8b9b4, 0.0);
    this.scene.background = this.scene.fog.color;

    this.camera = new THREE.PerspectiveCamera(40, 1, 0.1, 2000);
    // this.camera = new THREE.OrthographicCamera(-1, 1, 1, 1, -1000, 1000);
    this.renderer = new THREE.WebGLRenderer({
      alpha: false,
      stencil: false,
      antialias: true,
      powerPreference: 'high-performance',
    });
    this.renderer.autoClearColor = false;
    this.renderer.setPixelRatio(window.devicePixelRatio);

    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.outputEncoding = THREE.sRGBEncoding;

    wrapper.appendChild(this.renderer.domElement);

    // const geometry = new THREE.BoxGeometry();
    // const material = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
    // const cube = new THREE.Mesh(geometry, material);
    // this.scene.add(cube);

    // LIGHTS

    // Hemi light

    const hemiLight = new THREE.HemisphereLight(0xffffff, 0xffffff, 0.6);
    hemiLight.color.setHSL(0.6, 1, 0.6);
    hemiLight.groundColor.setHSL(0.095, 1, 0.75);
    // hemiLight.position.set(0, 150, 0);
    this.scene.add(hemiLight);
    this.hemiLight = hemiLight;

    // Directional light

    const worldCenterX = (100 * CELL_SIZE_METERS) / 2;
    const worldCenterY = (50 * CELL_SIZE_METERS) / 2;

    const dirLight = new THREE.DirectionalLight(0xffffff, 1);
    dirLight.color.setHSL(0.1, 1, 0.95);
    dirLight.position.set(0, 200, 0);
    dirLight.target.position.set(worldCenterX, 0, worldCenterY);

    // dirLight.position.multiplyScalar(30);
    this.scene.add(dirLight);
    this.scene.add(dirLight.target);

    dirLight.castShadow = true;
    dirLight.shadow.mapSize.width = 2048;
    dirLight.shadow.mapSize.height = 2048;
    const d = 250;
    dirLight.shadow.camera.left = -d / 2;
    dirLight.shadow.camera.right = +d;
    dirLight.shadow.camera.top = +d;
    dirLight.shadow.camera.bottom = -d / 2;

    dirLight.shadow.camera.near = 150;
    dirLight.shadow.camera.far = 600;
    dirLight.shadow.bias = -0.005;

    // const dirLightCameraHelper = new THREE.CameraHelper(dirLight.shadow.camera);
    // this.scene.add(dirLightCameraHelper);

    // GROUND
    const groundGeo = new THREE.PlaneBufferGeometry(10000, 10000);
    const groundMat = new THREE.MeshLambertMaterial({ color: 0xffffff });
    groundMat.color.setHSL(0.095, 1, 0.75);

    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.position.y = -0.1;
    ground.rotation.x = -Math.PI / 2;
    // ground.receiveShadow = true;
    // this.scene.add(ground);

    // SKYDOME

    const uniforms = {
      topColor: { value: new THREE.Color(0x0077ff) },
      bottomColor: { value: new THREE.Color(0xffffff) },
      offset: { value: 33 },
      exponent: { value: 0.6 },
    };
    uniforms['topColor'].value.copy(hemiLight.color);

    this.scene.fog.color.copy(uniforms['bottomColor'].value);

    const skyGeo = new THREE.SphereBufferGeometry(1000, 100, 100);
    const skyMat = new THREE.ShaderMaterial({
      uniforms: uniforms,
      vertexShader: skyVertexShader,
      fragmentShader: skyFragmentShader,
      side: THREE.BackSide,
    });

    const sky = new THREE.Mesh(skyGeo, skyMat);
    this.scene.add(sky);

    // GRID
    if (this.props.grid) {
      const gridHelper = new THREE.GridHelper(1000, 200);
      this.scene.add(gridHelper);
    }

    // ENTITIES

    this.entities = new THREE.Group();
    this.scene.add(this.entities);

    // SELECTION

    this.selection = new THREE.Group();
    this.scene.add(this.selection);

    if (this.props.cameraPosition) {
      this._setCameraFromProps();
      // const pos = this.props.cameraPosition;
      // const rot = this.props.cameraRotation;
      // console.log(pos, rot);
      // this.camera.position.set(pos[0], pos[1], pos[2]);
      // var angle = new THREE.Euler(rot[0], rot[1], rot[2]);
      // this.camera.rotation.copy(angle);
      // // this.camera.rotation.setFromVector3(
      // //   new THREE.Vector3(this.props.cameraRotation[0], this.props.cameraRotation[1], this.props.cameraRotation[2])
      // // );
      // // this.controls = new OrbitControls(this.camera, this.renderer.domElement);
      // // this.controls.update();
      // console.log(this.camera);
    } else {
      this.camera.position.z = 500;
      this.camera.position.x = 250;
      this.camera.position.y = 200;
      // this.camera.rotation.setFromVector3(new THREE.Vector3(Math.PI, 0, 0));
    }
    if (this.props.controlsEnabled) {
      this.controls = new OrbitControls(this.camera, this.renderer.domElement);
      this.controls.target.x = 250;
      this.controls.target.z = 200;
      this.controls.update();
      this.controls.addEventListener('end', () => {
        // console.log(this.camera.position, this.camera.rotation);
        this.props.onChangeCamera && this.props.onChangeCamera(this.camera.position, this.camera.rotation);
      });
    }
    this.camera.updateProjectionMatrix();

    window.addEventListener('resize', this._onResize);
    this.renderer.domElement.addEventListener('mousedown', this._onMouseDown.bind(this));
    this.renderer.domElement.addEventListener('mousemove', this._onMouseMove.bind(this));
    this.renderer.domElement.addEventListener('mouseup', this._onMouseUp.bind(this));
    this.renderer.domElement.addEventListener('wheel', this._onMouseWheel.bind(this));

    this._onResize();
    this._rebuildScene();
    this._renderScene();
  }

  _setCameraFromProps() {
    let pos = this.props.cameraPosition;
    this.targetCameraPosition = new THREE.Vector3(pos[0], pos[1], pos[2]);
    let rot = this.props.cameraRotation;
    rot = new THREE.Euler(rot[0], rot[1], rot[2]);
    this.targetCameraQuaternion = new THREE.Quaternion();
    this.targetCameraQuaternion.setFromEuler(rot);
    if (!this.props.disableCameraAnimation) {
      this._animateCamera();
    } else {
      this.camera.position.copy(this.targetCameraPosition);
      this.camera.quaternion.copy(this.targetCameraQuaternion);
      this._renderScene();
    }
  }

  _animateCamera() {
    this.camera.position.lerp(this.targetCameraPosition, 0.04);
    this.camera.quaternion.slerp(this.targetCameraQuaternion, 0.04);
    this._renderScene();

    const dist = this.camera.position.distanceTo(this.targetCameraPosition);
    const angle = this.camera.quaternion.angleTo(this.targetCameraQuaternion);
    if (dist > 0.1 || angle > 0.01) {
      window.requestAnimationFrame(this._animateCamera);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this._onResize);
    this.controls && this.controls.dispose();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.version !== this.props.version ||
      prevProps.world !== this.props.world ||
      prevProps.assetLibrary !== this.props.assetLibrary
    ) {
      this._rebuildScene();
    }
    if (prevProps.selection !== this.props.selection) {
      this._rebuildSelection();
    }
    if (
      prevProps.cameraPosition !== this.props.cameraPosition ||
      prevProps.cameraRotation !== this.props.cameraRotation ||
      prevProps.cameraVersion !== this.props.cameraVersion
    ) {
      this._setCameraFromProps();
      // const pos = this.props.cameraPosition;
      // const rot = this.props.cameraRotation;
      // console.log(pos, rot);
      // this.camera.position.set(pos[0], pos[1], pos[2]);
      // var angle = new THREE.Euler();
      // angle.fromArray(rot);
      // this.camera.rotation.copy(angle);
    }
    this._renderScene();
  }

  _rebuildScene() {
    const { world, environment, assetLibrary } = this.props;
    const rng = seedrandom(42);

    if (!world || !assetLibrary) {
      return;
    }
    // Remove everything from the entities group
    while (this.entities.children.length) {
      this.entities.remove(this.entities.children[0]);
    }

    // Enable/disable boat layers if available.
    for (let i = 1; i <= 10; i++) {
      const boatLayer = world.layers.find((l) => l.name === `boot_${i}`);
      if (!boatLayer) continue;
      boatLayer.visible = environment.boats >= i * 10;
    }

    const mergedEntities = world.getMergedEntities();

    // Assets are drawn on a per-mesh basis, not on a per-entity basis.
    // E.g. all roads need to be rendered at once.
    // To do that, we first build a list of where to draw which asset.
    const assetMap = {};
    const entityMap = {};
    const cellMap = new Uint32Array(world.width * world.height);

    // Do a pre-pass for all entities. Entities that come first "reserve their spot".
    // This is checked later by per-cell entities to see if they can occupy a spot or not.
    // Multi-cell entities do not check this map because this was checked in getMergedEntities.
    for (const entity of mergedEntities) {
      for (const cellId of entity.cellIds) {
        cellMap[cellId] = entity.id;
      }
    }

    for (const entity of mergedEntities) {
      const assetMode = Entity.assetModeForKind(entity.kind);
      if (assetMode === ASSET_MODE_PER_ENTITY) {
        const assetId = entity.assetIds[0];
        const direction = entity.directions[0];
        const bounds = world.calculateBoundingBoxFast(entity.cellIds);
        const matrices = assetMap[assetId] || [];
        const entities = entityMap[assetId] || [];
        entities.push(entity);
        const m = new THREE.Matrix4();
        m.identity();
        m.setPosition(
          lerp(bounds.minX, bounds.maxX, 0.5) * CELL_SIZE_METERS + CELL_SIZE_METERS / 2,
          0,
          lerp(bounds.minY, bounds.maxY, 0.5) * CELL_SIZE_METERS + CELL_SIZE_METERS / 2
        );
        const rotMatrix = DIRECTION_MATRICES[direction];
        m.multiply(rotMatrix);
        matrices.push(m);
        assetMap[assetId] = matrices;
        entityMap[assetId] = entities;
      } else if (assetMode === ASSET_MODE_PER_CELL) {
        console.assert(entity.cellIds.length === entity.assetIds.length);
        for (let i = 0; i < entity.cellIds.length; i++) {
          const cellId = entity.cellIds[i];
          let assetId = entity.assetIds[i];
          // If this cell was merged with another entity, skip.
          if (cellMap[cellId] !== entity.id) continue;
          if (entity.kind === ENTITY_KIND_ROAD) {
            const pedestrians = binomialDistribution(environment.pedestrians, TRANSPORT_CLASSES.pedestrians, rng);
            const bikes = binomialDistribution(environment.bikes, TRANSPORT_CLASSES.bikes, rng);
            const cargoBikes = binomialDistribution(environment.cargoBikes, TRANSPORT_CLASSES.cargoBikes, rng);
            const cars = binomialDistribution(environment.cars, TRANSPORT_CLASSES.cars, rng);
            const trucks = binomialDistribution(environment.trucks, TRANSPORT_CLASSES.trucks, rng);
            const buses = binomialDistribution(environment.buses, TRANSPORT_CLASSES.buses, rng);
            const passengerTrains = binomialDistribution(environment.passengerTrains, TRANSPORT_CLASSES.passengerTrains, rng);
            const freightTrains = binomialDistribution(environment.freightTrains, TRANSPORT_CLASSES.freightTrains, rng);
            const baseAssetName = world.findAssetNameById(assetId);
            const variants = assetLibrary.variants[baseAssetName];
            // FIXME: if one of the parameters is higher than zero, make sure to get an asset that contains at least one of these values.
            const filteredAssets = variants.filter((asset) => {
              if (asset.passengerTrains > 0 || asset.freightTrains > 0) {
                if (passengerTrains > 0 && freightTrains > 0) {
                  return asset.passengerTrains === passengerTrains || asset.freightTrains === freightTrains;
                } else {
                  return asset.passengerTrains === passengerTrains && asset.freightTrains === freightTrains;
                }
              } else {
                return (
                  asset.pedestrians === pedestrians &&
                  asset.bikes === bikes &&
                  asset.cargoBikes === cargoBikes &&
                  asset.cars === cars &&
                  asset.trucks === trucks &&
                  asset.buses === buses
                );
              }
            });
            if (filteredAssets.length > 0) {
              // Choose a random asset based on the random generator.
              const assetName = filteredAssets[Math.floor(rng() * filteredAssets.length)].name;
              assetId = world.ensureAsset(assetName);
            }
          }
          const direction = entity.directions[i];
          const matrices = assetMap[assetId] || [];
          const entities = entityMap[assetId] || [];
          entities.push(entity);
          const m = new THREE.Matrix4();
          const pt = world.cellIdToPoint(cellId);
          m.setPosition(pt.x * CELL_SIZE_METERS + CELL_SIZE_METERS / 2, 0, pt.y * CELL_SIZE_METERS + CELL_SIZE_METERS / 2);
          const rotMatrix = DIRECTION_MATRICES[direction];
          m.multiply(rotMatrix);
          matrices.push(m);
          assetMap[assetId] = matrices;
          entityMap[assetId] = entities;
        }
      } else {
        throw new Error(`Invalid assetMode ${assetMode} for ${entity.id}.`);
      }
    }

    // We draw everything as InstancedMeshes based on the matrices we just gathered.
    for (const [assetId, matrices] of Object.entries(assetMap)) {
      const entities = entityMap[assetId];
      const assetName = world.findAssetNameById(assetId);
      const asset = assetLibrary.assets.find((asset) => asset.name === assetName);
      if (!asset) {
        console.warn(`Could not find asset ${assetName}.`);
        continue;
      }
      if (asset.mesh.type === 'Group') {
        for (const mesh of asset.mesh.children) {
          this._createInstancedMesh(mesh, entities, matrices);
        }
      } else if (asset.mesh.type === 'Mesh') {
        this._createInstancedMesh(asset.mesh, entities, matrices);
      }
    }

    // Set effects
    let fogDensity = (environment.cars + environment.trucks / 3 + environment.buses / 5) / 300;
    fogDensity *= this.props.fogDensityFactor === undefined ? 1 : this.props.fogDensityFactor;
    this.scene.fog.density = Math.sqrt(fogDensity) / 100;
  }

  _createInstancedMesh(mesh, entities, matrices) {
    const instancedMesh = new THREE.InstancedMesh(mesh.geometry, mesh.material, matrices.length);
    instancedMesh.receiveShadow = true;
    instancedMesh.castShadow = true;
    for (let i = 0; i < matrices.length; i++) {
      const m = matrices[i];
      instancedMesh.setMatrixAt(i, m);
    }
    instancedMesh.userData = entities;
    this.entities.add(instancedMesh);
  }

  _rebuildSelection() {
    while (this.selection.children.length) {
      this.selection.remove(this.selection.children[0]);
    }

    const entities = this.props.world.findEntitiesForCells(this.props.selection);
    for (const mesh of this.entities.children) {
      if (entities.has(mesh.userData)) {
        const box = new THREE.BoxHelper(mesh, 0xffff00);
        this.selection.add(box);
      }
    }
  }

  _onResize() {
    const canvasWrapper = this.renderer.domElement.parentElement;
    const bounds = canvasWrapper.getBoundingClientRect();
    const width = bounds.width;
    const height = bounds.height;
    const aspect = width / height;

    if (this.camera.type === 'OrthographicCamera') {
      this.camera.left = (-aspect * height) / 2;
      this.camera.right = (aspect * height) / 2;
      this.camera.top = height / 2;
      this.camera.bottom = -height / 2;
    } else {
      this.camera.aspect = aspect;
    }

    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
    this._renderScene();
  }

  _onMouseDown(e) {}

  _onMouseMove() {
    this._renderScene();
  }

  _onMouseUp(e) {
    this.setState({ camera: this.camera });
    if (e.button !== 0) return;
    const mouse = new THREE.Vector3(
      (e.offsetX / (e.target.width / window.devicePixelRatio)) * 2 - 1,
      -(e.offsetY / (e.target.height / window.devicePixelRatio)) * 2 + 1,
      0.5
    );
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, this.camera);
    const intersects = raycaster.intersectObjects(this.entities.children);
    if (intersects.length) {
      const userData = intersects[0].object.userData;
      if (Array.isArray(userData)) {
        const instanceId = intersects[0].instanceId;
        const entity = userData[instanceId];
        this.props.onSelectEntity && this.props.onSelectEntity(entity);
      }
    }
  }

  _onMouseWheel() {
    this._renderScene();
    this.setState({ camera: this.camera });
  }

  _renderScene() {
    this.renderer.render(this.scene, this.camera);
  }

  _copyCameraData() {
    const { camera } = this.state;
    const pos = camera.position;
    const rot = camera.rotation;

    const data = `  cameraPosition: [${pos.x.toFixed(2)}, ${pos.y.toFixed(2)}, ${pos.z.toFixed(2)}],
  cameraRotation: [${rot.x.toFixed(2)}, ${rot.y.toFixed(2)}, ${rot.z.toFixed(2)}],`;
    copyText(data);
  }

  render() {
    const { camera } = this.state;
    let cameraDiv;
    if (camera) {
      cameraDiv = (
        <div className="viewport__camera" onClick={() => this._copyCameraData()}>
          POS {camera.position.x.toFixed(2)} {camera.position.y.toFixed(2)} {camera.position.z.toFixed(2)} ROT
          {camera.rotation.x.toFixed(2)} {camera.rotation.y.toFixed(2)} {camera.rotation.z.toFixed(2)}
        </div>
      );
    }
    return <div className="viewport__wrapper">{cameraDiv}</div>;
  }
}
